import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { styled } from "@mui/material/styles";

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "8px",
    minWidth: 160,
    backgroundColor: "#ffffff",
    boxShadow:
      "0px 4px 20px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    "& .MuiMenuItem-root": {
      fontSize: "14px",
      padding: "8px 16px",
      minHeight: "auto",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(39, 137, 253, 0.08)",
      },
      "&.Mui-disabled": {
        opacity: 0.5,
      },
      "& .MuiListItemIcon-root": {
        minWidth: "32px",
        "& .MuiSvgIcon-root": {
          fontSize: "18px",
        },
      },
      "& .MuiListItemText-primary": {
        fontSize: "14px",
        fontWeight: 500,
      },
    },
    "& .MuiDivider-root": {
      margin: "4px 0",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme, ownerState }) => {
  const bgColor = ownerState?.bgColor ?? "transparent";
  const hoverBgColor = ownerState?.hoverBgColor ?? "rgba(39, 137, 253, 0.08)";
  const iconColor = ownerState?.iconColor ?? "#6b7280";

  return {
    width: 32,
    height: 32,
    padding: 4,
    color: iconColor,
    backgroundColor: bgColor,
    border: "1px solid transparent",
    borderRadius: "6px",
    transition: "all 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: hoverBgColor,
      color: ownerState?.hoverColor ?? "#2789fd",
      transform: "scale(1.05)",
      border: "1px solid rgba(39, 137, 253, 0.12)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
    },
  };
});

/**
 * Universal Table Action Dropdown Component - how to use (useCase for developer))
 * const actions = [
 *   {
 *     icon: <EditIcon />,
 *     label: "Edit Task",
 *     onClick: (rowData) => handleEdit(rowData),
 *   },
 *   {
 *     icon: <VisibilityIcon />,
 *     label: "View Details",
 *     onClick: (rowData) => handleView(rowData),
 *   },
 *   {
 *     icon: <DeleteIcon />,
 *     label: "Delete",
 *     onClick: (rowData) => handleDelete(rowData),
 *     color: "#dc2626",
 *     divider: true,
 *   },
 * ];
 *
 * // Optional props: buttonBg (string) - background color for the button
 * // buttonHoverBg (string) - hover background color for the button
 *
 * // Usage in table column
 * {
 *   header: "Actions",
 *   Cell: ({ cell }) => (
 *     <TableActionDropdown
 *       actions={actions}
 *       rowData={cell.row.original}
 *       buttonBg="#f5f7fb"
 *       buttonHoverBg="rgba(39,137,253,0.08)"
 *     />
 *   ),
 * }
 */

const TableActionDropdown = ({
  actions = [],
  rowData = {},
  disabled = false,
  tooltipText = "More actions",
  buttonBg = "#f5f7fb",
  buttonHoverBg = "rgba(39, 137, 253, 0.08)",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (disabled || actions.length === 0) return;
    event.stopPropagation(); // Prevent row click events
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation(); // Prevent row click events
    }
    setAnchorEl(null);
  };

  const handleActionClick = (action, event) => {
    event.stopPropagation(); // Prevent row click events
    handleClose();

    if (action.onClick && !action.disabled) {
      action.onClick(rowData);
    }
  };

  // Don't render if no actions provided
  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <Box>
      <StyledIconButton
        ownerState={{ bgColor: buttonBg, hoverBgColor: buttonHoverBg }}
        onClick={handleClick}
        disabled={disabled}
        title={tooltipText}
        aria-label={tooltipText}
        aria-controls={open ? "table-action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreHorizIcon />
      </StyledIconButton>

      <StyledMenu
        id="table-action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()} // Prevent row click events
        MenuListProps={{
          "aria-labelledby": "table-action-button",
          dense: true,
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        {actions.map((action, index) => {
          const isDisabled = action.disabled || disabled;

          return [
            <MenuItem
              key={`action-${index}`}
              onClick={(event) => handleActionClick(action, event)}
              disabled={isDisabled}
              sx={{
                color: action.color || "inherit",
                "&:hover": {
                  backgroundColor: action.color
                    ? `${action.color}10`
                    : "rgba(39, 137, 253, 0.08)",
                },
              }}
            >
              {action.icon && (
                <ListItemIcon
                  sx={{
                    color: isDisabled ? "inherit" : action.color || "inherit",
                  }}
                >
                  {action.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={action.label}
                sx={{
                  color: isDisabled ? "inherit" : action.color || "inherit",
                }}
              />
            </MenuItem>,
            action.divider && index < actions.length - 1 && (
              <Divider key={`divider-${index}`} />
            ),
          ];
        })}
      </StyledMenu>
    </Box>
  );
};

export default TableActionDropdown;